// Azure Speech in Studio: dictation into the message box, and VERA's answers read aloud.
// The control plane hands out a short-lived token; the Speech key never reaches this app.

import { cpFetch } from "./controlPlane";

type Token = { token: string; region: string; expiresInSeconds: number };
let cached: (Token & { at: number }) | null = null;

export async function speechToken(getToken: () => Promise<string | null>): Promise<Token> {
  if (cached && Date.now() - cached.at < (cached.expiresInSeconds - 30) * 1000) return cached;
  const t = await cpFetch<Token>(getToken, "/v1/speech/token", { method: "POST", body: "{}" });
  cached = { ...t, at: Date.now() };
  return t;
}

/** Plain text for speaking: no Markdown symbols, links or code. */
export function speakableText(markdown: string, max = 4000): string {
  return markdown
    .replace(/```[\s\S]*?```/g, " ")
    .replace(/!\[[^\]]*]\([^)]*\)/g, " ")
    .replace(/\[([^\]]+)]\([^)]*\)/g, "$1")
    .replace(/^\s{0,3}#{1,6}\s*/gm, "")
    .replace(/^\s*[-*+]\s+/gm, "")
    .replace(/^\s*\|.*\|\s*$/gm, " ")
    .replace(/[*_`>#|]/g, "")
    .replace(/\s+/g, " ")
    .trim()
    .slice(0, max);
}

const esc = (s: string) => s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

/** Reads text aloud with a multilingual neural voice (English, German, Albanian and more). Returns a stop function. */
export async function speak(getToken: () => Promise<string | null>, text: string, onEnd?: () => void): Promise<() => void> {
  const { token, region } = await speechToken(getToken);
  const ssml = `<speak version="1.0" xml:lang="en-US"><voice name="en-US-AvaMultilingualNeural">${esc(speakableText(text))}</voice></speak>`;
  const res = await fetch(`https://${region}.tts.speech.microsoft.com/cognitiveservices/v1`, {
    method: "POST",
    headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/ssml+xml", "X-Microsoft-OutputFormat": "audio-24khz-48kbitrate-mono-mp3" },
    body: ssml,
  });
  if (!res.ok) throw new Error("Reading aloud didn't work. Please try again.");
  const url = URL.createObjectURL(await res.blob());
  const audio = new Audio(url);
  const done = () => { URL.revokeObjectURL(url); onEnd?.(); };
  audio.onended = done;
  audio.onerror = done;
  await audio.play();
  return () => { audio.pause(); done(); };
}

/** Continuous dictation; each finished phrase goes to onText. Detects English, German or Albanian. Returns a stop function. */
export async function dictate(getToken: () => Promise<string | null>, onText: (text: string) => void, onError: (message: string) => void): Promise<() => void> {
  const sdk = await import("microsoft-cognitiveservices-speech-sdk");
  const { token, region } = await speechToken(getToken);
  const cfg = sdk.SpeechConfig.fromAuthorizationToken(token, region);
  cfg.setProperty(sdk.PropertyId.SpeechServiceConnection_LanguageIdMode, "Continuous");
  const languages = sdk.AutoDetectSourceLanguageConfig.fromLanguages(["en-US", "de-CH", "sq-AL"]);
  const recognizer = sdk.SpeechRecognizer.FromConfig(cfg, languages, sdk.AudioConfig.fromDefaultMicrophoneInput());
  recognizer.recognized = (_s, e) => { if (e.result.reason === sdk.ResultReason.RecognizedSpeech && e.result.text) onText(e.result.text); };
  recognizer.canceled = (_s, e) => { if (e.reason === sdk.CancellationReason.Error) onError("Dictation stopped. Please try again."); };
  await new Promise<void>((resolve, reject) => recognizer.startContinuousRecognitionAsync(resolve, (err) => reject(new Error(String(err)))));
  return () => recognizer.stopContinuousRecognitionAsync(() => recognizer.close(), () => recognizer.close());
}
